import React, { lazy, useCallback, useEffect, useRef, useState } from "react"
import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import { useSpotlight } from "./SpotlightContext"
import { useTerminal } from "../terminal/TerminalContext"
import { WORKSPACE_LIST } from "../workspace/workspaces"
import { isProductivityHubEnabled, loadDesktopPrefs } from "../desktop/desktopStorage"
import { DESKTOP_LAYERS } from "../desktop/desktopLayers"

// ── Window definitions ─────────────────────────────────────────────────────────
// Exported so FloatingWindow.jsx can reference the component per typeId.

const HOLOS_RULES_DEF = {
  id: "holos-rules", label: "HOLOS REGELN", desc: "Fenster automatisch anordnen und skalieren", color: "var(--app-info)",
  keywords: ["regeln", "rules", "fenster", "automation", "layout"],
  component: lazy(() => import("../desktop/DesktopRulesWindow")),
}
const DEPLOY_DEF = {
  id: "deploy", label: "DEPLOY", desc: "AI Assistant oder Hut & Heroes deployen", color: "var(--app-success)",
  keywords: ["deploy", "build", "ai assistant", "huts heroes", "production"],
  component: lazy(() => import("../desktop/DesktopDeployWindow")),
}
const HOLOS_WEB_DEFS = [
  { id: "holos-workspaces", label: "ARBEITSBEREICHE", desc: "SplitScreen-Cockpits speichern und aktivieren", color: "var(--app-info)", keywords: ["splitscreen", "workspace", "cockpit", "layout"], component: lazy(() => import("../desktop/HolosWorkspacesWindow")) },
  { id: "ai-control", label: "AI CONTROL CENTER", desc: "KI-Aktivität, Agenten und Budgets", color: "var(--feature-vault)", keywords: ["ai", "ki", "usage", "tokens", "claude", "codex", "hermes"], component: lazy(() => import("../desktop/HolosAIControlWindow")) },
  { id: "global-search", label: "GLOBAL SEARCH", desc: "Apps, Vault, Dateien und Notizen durchsuchen", color: "var(--feature-stats)", keywords: ["global", "search", "suche", "dateien"], component: lazy(() => import("../desktop/HolosGlobalSearchWindow")) },
  { id: "shared-workspace", label: "SHARED WORKSPACE", desc: "Gemeinsames Live-Board in Holos-Fenstern", color: "var(--feature-tests)", keywords: ["shared", "workspace", "board", "team"], component: lazy(() => import("../desktop/HolosSharedWorkspaceWindow")) },
  { id: "resource-governor", label: "RESOURCE GOVERNOR", desc: "Browserlast und Fensterressourcen steuern", color: "var(--app-warning)", keywords: ["resource", "performance", "ram", "memory", "speed"], component: lazy(() => import("../desktop/HolosResourceGovernorWindow")) },
  { id: "permission-center", label: "PERMISSION CENTER", desc: "Browserrechte und Holos-Module verwalten", color: "var(--feature-mail)", keywords: ["permission", "rechte", "security", "zugriff"], component: lazy(() => import("../desktop/HolosPermissionCenterWindow")) },
  { id: "app-studio", label: "APP STUDIO", desc: "Eigene Holos-Apps aus visuellen Blöcken", color: "var(--feature-analysis)", keywords: ["app", "studio", "builder", "widgets", "visual"], component: lazy(() => import("../desktop/HolosAppStudioWindow")) },
  { id: "coding-calendar", label: "CODING KALENDER", desc: "Commits, Code-Aktivität und Streaks als Heatmap", color: "var(--app-success)", keywords: ["coding", "kalender", "git", "commits", "heatmap", "streak"], component: lazy(() => import("../desktop/HolosCodingCalendarWindow")) },
]

export const WINDOW_DEFS = [
  // Combined workspaces (segmented toggle): Assistants, Knowledge, Dev.
  ...WORKSPACE_LIST.map((ws) => ({
    id: ws.id,
    label: ws.label,
    desc: ws.desc,
    color: ws.color,
    keywords: ws.keywords,
    tabs: ws.tabs,
  })),
  {
    id: "jarvis",
    label: "JARVIS",
    desc: "JARVIS Chat",
    color: "var(--app-accent)",
    keywords: ["jarvis", "assistant", "chat"],
    component: lazy(() => import("../chat/JarvisPage")),
  },
  {
    id: "friday",
    label: "FRIDAY",
    desc: "FRIDAY Chat",
    color: "var(--agent-friday)",
    keywords: ["friday", "assistant", "chat"],
    component: lazy(() => import("../chat/FridayPage")),
  },
  {
    id: "hermes",
    label: "HERMES",
    desc: "Orchestrator & Agenten",
    color: "var(--feature-hermes)",
    keywords: ["hermes", "orchestrator", "agenten"],
    component: lazy(() => import("../hermes/HermesPage")),
  },
  {
    id: "research",
    label: "DEEP RESEARCH",
    desc: "Veronica Research",
    color: "var(--feature-research)",
    keywords: ["research", "forschung", "veronica"],
    component: lazy(() => import("../deep-research/DeepResearchPage")),
  },
  {
    id: "vault",
    label: "VAULT",
    desc: "Wissensgraph",
    color: "var(--feature-vault)",
    keywords: ["vault", "wissen", "graph"],
    component: lazy(() => import("../vault/VaultPage")),
  },
  {
    id: "file-manager",
    label: "DATEIMANAGER",
    desc: "Vault-Dateien und Ordner",
    color: "var(--app-warning)",
    keywords: ["dateien", "ordner", "files", "vault", "manager"],
    component: lazy(() => import("../desktop/DesktopFileManagerWindow")),
  },
  {
    id: "database",
    label: "DATENBANK",
    desc: "Datenbank-Explorer",
    color: "var(--feature-database)",
    keywords: ["database", "datenbank", "sql"],
    component: lazy(() => import("../database/DatabasePage")),
  },
  {
    id: "ide",
    label: "IDE",
    desc: "Code & Projekte",
    color: "var(--app-info)",
    keywords: ["ide", "code", "editor"],
    component: lazy(() => import("../ide/IDEPage")),
  },
  {
    id: "hut-game",
    label: "HUT & HEROES",
    desc: "Idle-RPG: Helden, Teams & Abenteuer",
    color: "var(--feature-hut)",
    keywords: ["hut", "game", "heroes", "idle", "rpg"],
    component: lazy(() => import("../hut-frame/HutGamePage")),
  },
  {
    id: "stats",
    label: "STATS",
    desc: "Systemstatistiken",
    color: "var(--feature-stats)",
    keywords: ["stats", "statistik", "monitoring", "s"],
    component: lazy(() => import("../stats/StatsPage")),
  },
  {
    id: "dashboard",
    label: "DASHBOARD",
    desc: "Control Center & Widgets",
    color: "var(--app-text)",
    keywords: ["dashboard", "home", "widgets", "control", "center"],
    component: lazy(() => import("../home/HomeDashboard")),
  },
  {
    id: "mail",
    label: "MAIL",
    desc: "Postfach & Nachrichten",
    color: "var(--feature-mail)",
    keywords: ["mail", "email", "postfach", "nachrichten"],
    component: lazy(() => import("../mail/MailPage")),
  },
  {
    id: "live-chat",
    label: "LIVE CHAT",
    desc: "Nachrichten, Gruppen & Anrufe",
    color: "var(--app-warning)",
    keywords: ["chat", "messenger", "nachrichten", "gruppen", "anrufe"],
    component: lazy(() => import("../live-chat/LiveChatWindow")),
  },
  {
    id: "reminder",
    label: "REMINDER",
    desc: "Aufgaben & Kalender",
    color: "var(--feature-reminder)",
    keywords: ["reminder", "aufgaben", "todo", "kalender"],
    component: lazy(() => import("../reminder/ReminderPage")),
  },
  {
    id: "chill",
    label: "CHILL",
    desc: "LoFi · Ambient · Focus",
    color: "var(--feature-chill)",
    keywords: ["chill", "lofi", "musik", "music", "ambient", "focus", "noise", "c"],
    component: lazy(() => import("../chill/ChillPage")),
  },
  {
    id: "settings",
    label: "SETTINGS",
    desc: "Einstellungen & Konfiguration",
    color: "var(--app-text-dim)",
    keywords: ["settings", "einstellungen", "konfig", "ssh", "s"],
    component: lazy(() => import("../settings/SettingsPage")),
  },
  {
    id: "desktop-settings",
    label: "HOLOS EINSTELLUNGEN",
    desc: "Desktop, Hintergrund & Verhalten",
    color: "var(--app-text-dim)",
    keywords: ["holos", "desktop", "einstellungen", "wallpaper", "theme"],
    component: lazy(() => import("../desktop/DesktopSettingsWindow")),
  },
  {
    id: "browser",
    label: "HOLOS BROWSER",
    desc: "Google & Web",
    color: "var(--app-info)",
    keywords: ["browser", "google", "web", "internet", "suche"],
    component: lazy(() => import("../desktop/DesktopBrowserWindow")),
  },
  {
    id: "productivity",
    label: "HOLOS PRODUKTIVITÄT",
    desc: "Sitzungen, Routinen, Notizen & Makros",
    color: "var(--feature-tests)",
    keywords: ["produktivität", "sessions", "sitzungen", "routinen", "notizen", "makros"],
    component: lazy(() => import("../desktop/HolosProductivityWindows").then((module) => ({ default: module.ProductivityHubWindow }))),
  },
  {
    id: "universal-inbox",
    label: "UNIVERSAL INBOX",
    desc: "Mail, Chat, Reminder & Agenten",
    color: "#fb7185",
    keywords: ["inbox", "mail", "chat", "reminder", "aktivitäten"],
    component: lazy(() => import("../desktop/HolosProductivityWindows").then((module) => ({ default: module.UniversalInboxWindow }))),
  },
  {
    id: "agent-control",
    label: "AGENT CONTROL",
    desc: "Agenten, Queue & Aufgaben-Drop",
    color: "var(--app-warning)",
    keywords: ["agents", "agenten", "queue", "tasks", "delegieren"],
    component: lazy(() => import("../desktop/HolosProductivityWindows").then((module) => ({ default: module.AgentControlWindow }))),
  },
  {
    id: "mission-control",
    label: "MISSION CONTROL",
    desc: "Desktops, Fenster & Aktivitäten",
    color: "var(--feature-analysis)",
    keywords: ["mission", "control", "fenster", "desktops", "übersicht"],
    component: lazy(() => import("../desktop/HolosProductivityWindows").then((module) => ({ default: module.MissionControlWindow }))),
  },
  ...HOLOS_WEB_DEFS,
  HOLOS_RULES_DEF,
  DEPLOY_DEF,
  {
    id: "terminal",
    label: "TERMINAL",
    desc: "SSH Terminal",
    color: "var(--app-success)",
    keywords: ["terminal", "ssh", "shell", "t"],
    component: null,
  },
]

const FEATURE_WINDOWS = {
  "live-chat": "chatWindowEnabled",
  "universal-inbox": "inboxEnabled",
  "agent-control": "agentControlEnabled",
  "mission-control": "missionControlEnabled",
  "holos-workspaces": "splitWorkspacesEnabled",
  "holos-rules": "ruleBookEnabled",
  "ai-control": "aiControlEnabled",
  "file-manager": "holosFilesEnabled",
  "global-search": "globalSearchEnabled",
  "shared-workspace": "sharedWorkspaceEnabled",
  "resource-governor": "resourceGovernorEnabled",
  "permission-center": "permissionCenterEnabled",
  "app-studio": "appStudioEnabled",
  "coding-calendar": "codingCalendarEnabled",
}

function enabledDefinitions(prefs) {
  return WINDOW_DEFS.filter((definition) => {
    if (definition.id === "productivity") return isProductivityHubEnabled(prefs)
    return !FEATURE_WINDOWS[definition.id] || prefs[FEATURE_WINDOWS[definition.id]] !== false
  })
}

// ── Fuzzy match ────────────────────────────────────────────────────────────────

function score(def, query) {
  if (!query) return 1
  const q = query.toLowerCase()
  const label = def.label.toLowerCase()
  const desc  = def.desc.toLowerCase()
  const kws   = def.keywords

  if (label.startsWith(q))           return 100
  if (label.includes(q))             return 80
  if (desc.includes(q))              return 60
  if (kws.some((k) => k.startsWith(q))) return 70
  if (kws.some((k) => k.includes(q)))   return 50
  // char-by-char fuzzy
  let qi = 0
  for (let i = 0; i < label.length && qi < q.length; i++) {
    if (label[i] === q[qi]) qi++
  }
  if (qi === q.length) return 30
  return 0
}

function getResults(query, definitions = WINDOW_DEFS) {
  return definitions
    .map((d) => ({ ...d, _score: score(d, query) }))
    .filter((d) => d._score > 0)
    .sort((a, b) => b._score - a._score)
    .slice(0, 5)
}

// ── Black hole visual layers ───────────────────────────────────────────────────

const BH_KEYFRAMES = {
  "@keyframes bh-spin": {
    to: { transform: "translate(-50%, -50%) rotate(360deg)" },
  },
  "@keyframes bh-pulse": {
    "0%, 100%": { opacity: 0.7, transform: "translate(-50%, -50%) scale(1)" },
    "50%":       { opacity: 1,   transform: "translate(-50%, -50%) scale(1.06)" },
  },
  "@keyframes bh-ring": {
    "0%, 100%": {
      boxShadow: "0 0 0 1px color-mix(in srgb, var(--app-accent) 35%, transparent), 0 0 30px color-mix(in srgb, var(--app-accent) 18%, transparent), 0 0 70px rgba(168,85,247,0.10), 0 20px 60px rgba(var(--app-shadow-rgb),0.85)",
    },
    "50%": {
      boxShadow: "0 0 0 1px color-mix(in srgb, var(--app-accent) 65%, transparent), 0 0 55px color-mix(in srgb, var(--app-accent) 32%, transparent), 0 0 110px rgba(168,85,247,0.18), 0 20px 60px rgba(var(--app-shadow-rgb),0.85)",
    },
  },
  "@keyframes bh-appear": {
    from: { opacity: 0, transform: "scale(0.94) translateY(-8px)" },
    to:   { opacity: 1, transform: "scale(1) translateY(0)" },
  },
  "@keyframes bh-fade": {
    from: { opacity: 0 },
    to:   { opacity: 1 },
  },
}

// ── SpotlightSearch ────────────────────────────────────────────────────────────

export default function SpotlightSearch() {
  const { open, closeSpotlight, openWindow, recent, pruneWindows } = useSpotlight()
  const { openTerminal } = useTerminal()

  const [query, setQuery]     = useState("")
  const [selected, setSelected] = useState(0)
  const [desktopPrefs, setDesktopPrefs] = useState(loadDesktopPrefs)
  const inputRef              = useRef(null)

  const definitions = enabledDefinitions(desktopPrefs)
  const results = getResults(query, definitions)
  const recentDefs = recent
    .map((id) => definitions.find((d) => d.id === id))
    .filter(Boolean)
    .slice(0, 4)

  useEffect(() => {
    const valid = enabledDefinitions(desktopPrefs)
    pruneWindows(new Set(valid.map((definition) => definition.id)))
  }, [desktopPrefs, pruneWindows])

  useEffect(() => {
    const refresh = (event) => setDesktopPrefs(event.detail ?? loadDesktopPrefs())
    window.addEventListener("desktop-settings-changed", refresh)
    return () => window.removeEventListener("desktop-settings-changed", refresh)
  }, [])

  useEffect(() => {
    if (open) {
      setQuery("")
      setSelected(0)
      setTimeout(() => inputRef.current?.focus(), 60)
    }
  }, [open])

  const doOpen = useCallback((def) => {
    openWindow(def.id, openTerminal)
  }, [openWindow, openTerminal])

  const onKeyDown = useCallback((e) => {
    if (e.key === "Escape") { closeSpotlight(); return }
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setSelected((s) => Math.min(s + 1, results.length - 1))
      return
    }
    if (e.key === "ArrowUp") {
      e.preventDefault()
      setSelected((s) => Math.max(s - 1, 0))
      return
    }
    if (e.key === "Enter") {
      e.preventDefault()
      const def = results[selected]
      if (def) doOpen(def)
      return
    }
  }, [results, selected, doOpen, closeSpotlight])

  useEffect(() => { setSelected(0) }, [query])

  if (!open) return null

  const listToShow = query.trim() ? results : []

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: DESKTOP_LAYERS.commandCenter,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        pt: "17vh",
        backdropFilter: "blur(12px) brightness(0.6)",
        background: "radial-gradient(ellipse 90% 55% at 50% 38%, rgba(var(--app-shadow-rgb),0.18) 0%, rgba(8,8,15,0.97) 68%)",
        animation: "bh-fade 0.2s ease-out both",
        ...BH_KEYFRAMES,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) closeSpotlight() }}
    >
      {/* Accretion disk — rotating conic gradient ring */}
      <Box sx={{
        position: "absolute",
        top: "calc(17vh + 28px)",
        left: "50%",
        width: 560,
        height: 560,
        borderRadius: "50%",
        transform: "translate(-50%, -50%)",
        background: "conic-gradient(from 0deg, transparent 15%, color-mix(in srgb, var(--app-accent) 5%, transparent) 35%, rgba(168,85,247,0.09) 55%, color-mix(in srgb, var(--app-accent) 4%, transparent) 75%, transparent 100%)",
        animation: "bh-spin 14s linear infinite",
        pointerEvents: "none",
        filter: "blur(2px)",
      }} />

      {/* Outer glow / event horizon ring */}
      <Box sx={{
        position: "absolute",
        top: "calc(17vh + 28px)",
        left: "50%",
        width: 340,
        height: 340,
        borderRadius: "50%",
        transform: "translate(-50%, -50%)",
        background: "radial-gradient(circle, color-mix(in srgb, var(--app-accent) 6%, transparent) 0%, rgba(168,85,247,0.04) 40%, transparent 70%)",
        animation: "bh-pulse 3.5s ease-in-out infinite",
        pointerEvents: "none",
      }} />

      {/* Main panel */}
      <Box
        sx={{
          width: 520,
          maxWidth: "90vw",
          display: "flex",
          flexDirection: "column",
          borderRadius: 2,
          overflow: "hidden",
          border: "1px solid color-mix(in srgb, var(--app-accent) 25%, transparent)",
          backgroundColor: "rgba(8,8,18,0.97)",
          animation: "bh-ring 2.5s ease-in-out infinite, bh-appear 0.22s ease-out both",
          position: "relative",
          zIndex: 1,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search input */}
        <Box sx={{
          display: "flex",
          alignItems: "center",
          px: 2,
          py: 0,
          gap: 1.5,
          borderBottom: listToShow.length > 0 || (!query && recentDefs.length > 0)
            ? "1px solid rgba(var(--app-fg-rgb),0.07)"
            : "none",
        }}>
          <Typography sx={{
            fontFamily: "var(--app-font-mono)",
            fontSize: "0.9rem",
            color: "color-mix(in srgb, var(--app-accent) 60%, transparent)",
            userSelect: "none",
            flexShrink: 0,
          }}>
            ⌬
          </Typography>
          <Box
            ref={inputRef}
            component="input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Fenster öffnen…"
            spellCheck={false}
            autoComplete="off"
            sx={{
              flex: 1,
              background: "transparent",
              border: "none",
              outline: "none",
              color: "var(--app-text)",
              fontFamily: "var(--app-font-mono)",
              fontSize: "1rem",
              py: 1.5,
              "&::placeholder": { color: "rgba(var(--app-fg-rgb),0.22)" },
            }}
          />
          <Typography sx={{
            fontFamily: "var(--app-font-mono)",
            fontSize: "0.6rem",
            color: "rgba(var(--app-fg-rgb),0.2)",
            flexShrink: 0,
            userSelect: "none",
          }}>
            ESC
          </Typography>
        </Box>

        {/* Search results */}
        {listToShow.length > 0 && (
          <Box>
            {listToShow.map((def, i) => (
              <ResultRow
                key={def.id}
                def={def}
                active={i === selected}
                onHover={() => setSelected(i)}
                onClick={() => doOpen(def)}
              />
            ))}
          </Box>
        )}

        {/* Recent windows — shown when search is empty */}
        {!query.trim() && recentDefs.length > 0 && (
          <Box sx={{ pb: 0.5 }}>
            <Typography sx={{
              px: 2, pt: 1.25, pb: 0.5,
              fontFamily: "var(--app-font-mono)",
              fontSize: "0.6rem",
              letterSpacing: "0.12em",
              color: "rgba(var(--app-fg-rgb),0.2)",
              userSelect: "none",
            }}>
              ZULETZT GEÖFFNET
            </Typography>
            {recentDefs.map((def, i) => (
              <ResultRow
                key={def.id}
                def={def}
                active={i === selected}
                onHover={() => setSelected(i)}
                onClick={() => doOpen(def)}
                compact
              />
            ))}
          </Box>
        )}

        {/* Empty state */}
        {query.trim() && listToShow.length === 0 && (
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography sx={{
              fontFamily: "var(--app-font-mono)",
              fontSize: "0.75rem",
              color: "rgba(var(--app-fg-rgb),0.2)",
            }}>
              Keine Ergebnisse für "{query}"
            </Typography>
          </Box>
        )}

        {/* Keyboard hint */}
        <Box sx={{
          px: 2, py: 0.75,
          display: "flex",
          gap: 2,
          borderTop: "1px solid rgba(var(--app-fg-rgb),0.05)",
          backgroundColor: "rgba(var(--app-shadow-rgb),0.3)",
        }}>
          {[["↑↓", "navigieren"], ["↵", "öffnen"], ["ESC", "schließen"]].map(([key, hint]) => (
            <Box key={key} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography sx={{
                fontFamily: "var(--app-font-mono)",
                fontSize: "0.58rem",
                color: "rgba(var(--app-fg-rgb),0.4)",
                backgroundColor: "rgba(var(--app-fg-rgb),0.07)",
                px: 0.6, py: 0.2,
                borderRadius: 0.5,
                border: "1px solid rgba(var(--app-fg-rgb),0.1)",
              }}>
                {key}
              </Typography>
              <Typography sx={{
                fontFamily: "var(--app-font-mono)",
                fontSize: "0.58rem",
                color: "rgba(var(--app-fg-rgb),0.2)",
              }}>
                {hint}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  )
}

// ── Result row ─────────────────────────────────────────────────────────────────

function ResultRow({ def, active, onHover, onClick, compact }) {
  return (
    <Box
      onPointerEnter={onHover}
      onClick={onClick}
      sx={{
        px: 2,
        py: compact ? 0.75 : 1,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        cursor: "pointer",
        backgroundColor: active ? "rgba(var(--app-fg-rgb),0.06)" : "transparent",
        borderLeft: active ? `2px solid ${def.color}` : "2px solid transparent",
        transition: "background-color 0.1s",
        "&:hover": { backgroundColor: "rgba(var(--app-fg-rgb),0.05)" },
      }}
    >
      <Box sx={{
        width: 28, height: 28,
        borderRadius: 1,
        backgroundColor: `color-mix(in srgb, ${def.color} 9.4%, transparent)`,
        border: `1px solid color-mix(in srgb, ${def.color} 25.1%, transparent)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        boxShadow: active ? `0 0 10px color-mix(in srgb, ${def.color} 18.8%, transparent)` : "none",
      }}>
        <Typography sx={{
          fontFamily: "var(--app-font-mono)",
          fontSize: "0.65rem",
          fontWeight: 700,
          color: def.color,
        }}>
          {def.label[0]}
        </Typography>
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          fontFamily: "var(--app-font-mono)",
          fontSize: "0.78rem",
          fontWeight: 600,
          color: active ? "#fff" : "rgba(var(--app-fg-rgb),0.75)",
          letterSpacing: "0.04em",
        }}>
          {def.label}
        </Typography>
        {!compact && (
          <Typography sx={{
            fontFamily: "var(--app-font-mono)",
            fontSize: "0.62rem",
            color: "rgba(var(--app-fg-rgb),0.3)",
            mt: 0.1,
          }}>
            {def.desc}
          </Typography>
        )}
      </Box>

      {active && (
        <Typography sx={{
          fontFamily: "var(--app-font-mono)",
          fontSize: "0.6rem",
          color: "rgba(var(--app-fg-rgb),0.3)",
          flexShrink: 0,
        }}>
          ↵
        </Typography>
      )}
    </Box>
  )
}
